import React, { ReactNode } from 'react'
import styled, { css } from 'styled-components'
import ExpandMoreIcon from 'assets/icons/expand_arrow.svg'
import ExpandMoreBlueIcon from 'assets/icons/expand_arrow_blue.svg'
import { useToggleState } from 'lib/hooks/useToggleState'
import { Subtitle1 } from 'stories/elements/Typography/Text'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import useMedia from 'lib/hooks/useMedia'
import theme from 'stories/utils/theme'

interface AccordionProps {
    accentBlue?: boolean
    isExpanded?: boolean
    noPadding?: boolean
    hideBorder?: boolean
    isDarkTheme?: boolean
    noMargin?: boolean
}

const Accordion = styled.div<AccordionProps>`
    display: grid;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.content};
    border: ${({ hideBorder }) =>
        !hideBorder ? `3px solid ${theme.colors.accentBlue}` : `1px solid ${theme.colors.lightGray}`};
    padding: ${({ noPadding }) => (!noPadding ? 25 : 15)}px;
    margin: ${({ noMargin }) => (!noMargin ? `15px 10px` : 0)};
    transition: all 0.3s ease-in-out;
    .summary {
        cursor: pointer;
        width: 100%;
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 5px;
        justify-content: space-between;
        align-items: center;
        transition: all 0.3s ease-in-out;

        svg {
            transition: transform 0.3s ease-in-out;
            transform: ${({ isExpanded }) => `rotate(${isExpanded ? '180deg' : '0deg'})`};
        }
    }
    .details {
        transition: all 0.3s ease-out;
        overflow: hidden;
        ${({ isExpanded }) =>
            !isExpanded
                ? css`
                      height: 0px;
                      opacity: 0;
                  `
                : css`
                      padding-top: 20px;
                      min-height: 50px;
                      opacity: 1;
                  `};
    }

    ${({ accentBlue, isExpanded }) =>
        accentBlue && isExpanded
            ? css`
                  box-shadow: 4px 4px 0px ${theme.colors.accentBlue};
                  margin: 15px 16.0987px 15px 0; // right margin - 11.0987 (box-shadow) + 5 = 16.0987[px]
                  @media (max-width: 750px) {
                      margin: 15px 0;
                  }
              `
            : ''}
    ${({ isDarkTheme }) =>
        isDarkTheme
            ? css`
                  border: none;
              `
            : ''}
    
    ${({ isDarkTheme }) =>
        isDarkTheme
            ? css`
                  background-color: rgba(255, 255, 255, 0.1);

                  border: none;
              `
            : ''}
    ${({ hideBorder }) =>
        hideBorder
            ? css`
                  box-shadow: none;
              `
            : ''}
    ${({ noMargin }) =>
        noMargin
            ? css`
                  margin: 0;
              `
            : ''}
`

const TextWrapper = styled.div<AccordionProps>`
    text-align: left !important;
    display: inline;
    padding: 0;

    a,
    b {
        font-family: var(--font-open-sans);
        font-size: 16px;
        font-style: normal;
        font-weight: 650;
        line-height: 20px;
        letter-spacing: 0em;
        text-align: left;
        color: ${({ accentBlue }) => (accentBlue ? theme.colors.accentBlue : theme.colors.knowunityBlue)};
    }
`

interface Props extends AccordionProps {
    title: string
    text: JSX.Element
    children?: ReactNode
    openByDefault?: boolean
    onToggle?: () => void
}

export default function CollapsibleAccordion({
    title,
    text,
    accentBlue,
    isDarkTheme,
    children,
    noPadding,
    hideBorder,
    openByDefault,
    noMargin,
    onToggle,
}: Props) {
    const [expanded, toggleExpanded] = useToggleState(openByDefault || false)
    const md = useMedia(deviceSize.md)

    const returnTitleColor = () => {
        switch (true) {
            case accentBlue:
                return md ? theme.colors.subtitleBlack : theme.colors.accentBlue
            case isDarkTheme:
                return theme.colors.white
            default:
                return theme.colors.subtitleBlack
        }
    }

    const onToggleClick = () => {
        toggleExpanded()
        !expanded && onToggle && onToggle()
    }
    return (
        <Accordion
            key={`collapsible-panel-${title}`}
            accentBlue={!!accentBlue}
            isExpanded={expanded}
            noPadding={!!noPadding}
            hideBorder={hideBorder}
            isDarkTheme={isDarkTheme}
            noMargin={noMargin}
        >
            <div
                onClick={onToggleClick}
                className="summary"
                aria-controls={`collapsible-panel-summary-${title}`}
                id={`collapsible-panel-header-${title}`}
            >
                <Subtitle1 fontFamily="var(--font-inter)" fontWeight={500} color={returnTitleColor()}>
                    {title}
                </Subtitle1>
                {accentBlue ? <ExpandMoreIcon /> : <ExpandMoreBlueIcon />}
            </div>
            <div className="details">
                <TextWrapper accentBlue={!!accentBlue}>{text}</TextWrapper>
            </div>
            {children}
        </Accordion>
    )
}
